/**
 * The `<mod_capabilities>` system-prompt block: Mod's short standing prompt
 * (RFC 0334 3b).
 *
 * WHAT EVERY TURN NEEDS, AND NO MORE. This block once carried the how-to for
 * every capability, built in code, paid for on every turn and editable by
 * nobody. The how-to is now Mod's own skills (`mod-skills.ts`), listed with
 * every other skill and read when a task needs one. What stays is what an agent
 * needs before it knows which skill to open: what Mod is, and how to behave in
 * it.
 *
 * PROMPT TEXT, NOT A SKILL, because it is standing: every harness Mod drives
 * can read an appended system prompt, and this is the one piece of guidance
 * that must reach the model without it choosing to read anything.
 *
 * GATED ON THE TOOLS HELD. A session holding no Mod tool (a filesystem-only
 * toolset) gets nothing. A behaviour line that names a tool is emitted only
 * when the session holds it, so a read-only agent is not told to mark tasks
 * active.
 */

import { shortToolName } from './mod-skills'

/** Any one of these means the session works in Mod and gets the block. */
const MOD_TOOLS = [
  'list_tasks',
  'my_tasks',
  'read_task',
  'create_task',
  'update_task',
  'complete_task',
  'list_sessions',
  'read_session',
  'ask_user_question',
  'create_document',
  'write_document',
  'patch_document',
  'read_document',
  'read_history',
  'register_skill',
  'comment',
  'pass',
  'read_view',
  'set_view',
  'list_apps',
  'read_app',
]

/**
 * Build the block from the tool names a session actually holds.
 *
 * Names may carry a harness prefix (`mcp__mod__create_task`), and Claude
 * Code's native `AskUserQuestion` counts as `ask_user_question`, so a caller
 * passes its catalogue as-is. Returns '' when nothing matches.
 */
export function formatModCapabilities(toolNames: Iterable<string>): string {
  const held = new Set<string>()
  for (const raw of toolNames) held.add(shortToolName(raw))
  if (!MOD_TOOLS.some((t) => held.has(t))) return ''

  const lines = [
    'Mod is a shared workspace where people and agents work on one log. A',
    "World holds a person's projects. A project holds documents, tasks,",
    'sessions and apps, and everyone in it sees what you write there, on every',
    'device, after this turn ends. A space is the arrangement of panes a',
    'person is looking at.',
    '- Work in the open. A plan, a follow-up or a handoff goes in a task or',
    '  a document, not only in this conversation.',
  ]
  if (held.has('update_task') && held.has('complete_task'))
    lines.push(
      '- Keep tasks honest: active when you start one, completed with a note',
      '  that says what changed and what you verified.',
    )
  if (held.has('set_view'))
    lines.push(
      '- Open the page the work needs, so the person sees what you are doing.',
    )
  lines.push(
    held.has('ask_user_question')
      ? '- Ask one question at a time, with your pick, and only when a wrong'
      : '- Ask the person something only when a wrong',
    '  guess is expensive. Otherwise decide, say what you assumed, and go on.',
    '- How to use each of these is a Mod skill: mod-tasks, mod-questions,',
    '  mod-spaces, mod-documents and the rest. The first time in a session you',
    '  create or change a task, ask, open a page, or write a document, load the',
    '  matching one first (in Claude Code, `mod:mod-tasks` and so on with the',
    '  Skill tool). Do not skip it because the call looks simple.',
  )
  return `<mod_capabilities>\n${lines.join('\n')}\n</mod_capabilities>`
}
